import React, { useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Label,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { useStudyTimepoints } from "hooks/useStudyTimepoints";

import { PrintReportPayload } from "../api/printreport.api";
import {
  customSelectComponents,
  getCustomSelectStyles,
  handleAllMultiSelectChange,
  sanitizeSingleFilterVal,
  sanitizeMultiFilterVal,
} from "../helper/reportSelectUtils";

interface VacutainerReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStudy: any;
  onSubmit: (payload: PrintReportPayload) => void;
  isLoading: boolean;
}

interface VacutainerReportFormValues {
  studyid: number | string | null;
  period: string | null;
  timepoint: (string | number)[];
}

const VacutainerReportModal: React.FC<VacutainerReportModalProps> = ({
  isOpen,
  onClose,
  selectedStudy,
  onSubmit,
  isLoading,
}) => {
  const studyId = selectedStudy?.studyid ? Number(selectedStudy.studyid) : 0;
  const { dynamicPeriods, dynamicPhases } = useStudyTimepoints(studyId);

  const periodOptions = useMemo(() => {
    return dynamicPeriods.map((p: any) => ({
      label: String(p.periodorder).padStart(2, "0"),
      value: String(p.periodorder).padStart(2, "0"),
    }));
  }, [dynamicPeriods]);

  const timepointOptions = useMemo(() => {
    const opts = dynamicPhases.map((p: any) => ({
      label: p.label,
      value: p.label,
    }));
    return [{ label: "All Timepoints", value: "ALL" }, ...opts];
  }, [dynamicPhases]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm<VacutainerReportFormValues>({
    defaultValues: {
      studyid: studyId,
      period: null,
      timepoint: ["ALL"],
    },
  });

  const selectedPeriod = watch("period");
  const selectedTimepoint = watch("timepoint");
  const studyCodeLabel =
    selectedStudy?.studycode ||
    selectedStudy?.studyname ||
    sessionStorage.getItem("globalstudyname") ||
    "N/A";

  useEffect(() => {
    if (isOpen) {
      reset({
        studyid: studyId,
        period: periodOptions[0]?.value || null,
        timepoint: ["ALL"],
      });
    }
  }, [isOpen, studyId, periodOptions]);

  useEffect(() => {
    if (studyId) {
      setValue("studyid", studyId);
    }
  }, [studyId, setValue]);

  useEffect(() => {
    if (!selectedPeriod && periodOptions.length > 0) {
      setValue("period", periodOptions[0].value);
    }
  }, [selectedPeriod, periodOptions, setValue]);

  useEffect(() => {
    if (
      !selectedTimepoint ||
      (Array.isArray(selectedTimepoint) && selectedTimepoint.length === 0)
    ) {
      setValue("timepoint", ["ALL"]);
    }
  }, [selectedTimepoint, setValue]);

  const handleFormSubmit = (values: VacutainerReportFormValues) => {
    onSubmit({
      barcodetype: "VACUTAINER",
      studyid: studyId || null,
      periodnumber: sanitizeSingleFilterVal(values.period),
      timepointnumber: sanitizeMultiFilterVal(values.timepoint),
    });
  };

  const fields: FormFieldConfig[] = [
    {
      name: "studyid",
      label: "Study Code",
      type: "select",
      required: true,
      disabled: true,
      options: [{ label: studyCodeLabel, value: studyId }],
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="md"
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={onClose} className="p-3">
        <i className="ri-flask-line me-2 align-middle" />
        Vacutainer Print Report Filter
      </ModalHeader>

      <ModalBody>
        <div className="mb-3">
          <CommonForm
            fields={fields}
            control={control}
            errors={errors}
            columns={1}
            setValue={setValue}
            getValues={getValues}
          />
        </div>

        <div className="mb-3">
          <Label className="form-label mb-1" style={{ fontSize: "14px", fontWeight: 500 }}>
            Period <span className="text-danger">*</span>
          </Label>
          <Controller
            name="period"
            control={control}
            render={({ field: controllerField }) => {
              const isAll = controllerField.value === "ALL";
              const selectedOpt = periodOptions.find((opt: any) => opt.value === controllerField.value) || null;

              return (
                <Select
                  options={periodOptions}
                  value={selectedOpt}
                  onChange={(opt: any) => {
                    setValue("period", opt ? opt.value : "ALL", { shouldValidate: true, shouldDirty: true });
                  }}
                  placeholder="Select Period"
                  menuPortalTarget={document.body}
                  className="common-select"
                  classNamePrefix="common-select"
                  styles={getCustomSelectStyles(!!errors?.period, isAll)}
                />
              );
            }}
          />
        </div>

        <div className="mb-3">
          <Label className="form-label mb-1" style={{ fontSize: "14px", fontWeight: 500 }}>
            Timepoint <span className="text-danger">*</span>
          </Label>
          <Controller
            name="timepoint"
            control={control}
            render={({ field: controllerField }) => {
              const currentVals = Array.isArray(controllerField.value)
                ? controllerField.value
                : controllerField.value
                ? [controllerField.value]
                : ["ALL"];
              const selectedOpts = timepointOptions.filter((opt) =>
                currentVals.includes(opt.value)
              );
              const isAll = currentVals.includes("ALL");

              return (
                <Select
                  isMulti
                  options={timepointOptions}
                  value={selectedOpts}
                  onChange={(selected: any) =>
                    handleAllMultiSelectChange("timepoint", selected, currentVals, setValue)
                  }
                  placeholder="Select Timepoint"
                  menuPortalTarget={document.body}
                  className="common-select"
                  classNamePrefix="common-select"
                  components={customSelectComponents}
                  styles={getCustomSelectStyles(!!errors?.timepoint, isAll)}
                />
              );
            }}
          />
        </div>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="dark" outline onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="primary"
          outline
          onClick={handleSubmit(handleFormSubmit)}
          disabled={isLoading}
        >
          {isLoading && <Spinner size="sm" className="me-2" />}
          Generate Report
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default VacutainerReportModal;
